/** @format */

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import PhotoArmoireManager from "./PhotoArmoireManager";
import {
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  AlertCircle,
  Archive,
  Eye,
  Package,
  FolderOpen,
  Wrench,
  Gauge,
  Ruler,
  CircleDot,
  Stethoscope,
  Wind,
  Drill,
  Scissors,
  Hammer,
  CheckCircle,
  Zap,
  Cog,
  TrendingUp,
  Disc,
  Paintbrush,
  Flame,
  Layers,
  Sparkles,
  Shield,
  Droplet,
  MoreHorizontal,
  Camera,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

// Map pour convertir nom → composant icon
const IconMap = {
  Package,
  Wrench,
  Gauge,
  Ruler,
  CircleDot,
  Stethoscope,
  Wind,
  Drill,
  Scissors,
  Hammer,
  CheckCircle,
  Zap,
  Cog,
  TrendingUp,
  Archive,
  Disc,
  Paintbrush,
  Flame,
  Layers,
  Sparkles,
  Shield,
  Droplet,
  MoreHorizontal,
};

// Composant pour afficher une icône
const IconDisplay = ({ iconName, className = "w-4 h-4" }) => {
  const IconComponent = IconMap[iconName] || Package;
  return <IconComponent className={className} />;
};

const GestionEmplacements = ({
  onEditOutil,
  onRefresh,
  onAddOutilInArmoire,
}) => {
  const [ateliers, setAteliers] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [outils, setOutils] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingEmplacement, setViewingEmplacement] = useState(null);
  const [showPhotoArmoire, setShowPhotoArmoire] = useState(false);
  const [armoireSelectionnee, setArmoireSelectionnee] = useState(null);

  const defaultFormData = {
    atelier_id: "",
    categorie_id: "",
    type_emplacement: "armoire",
    nom: "",
    ordre_affichage: 1,
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    try {
      setLoading(true);

      const { data: ateliersData, error: ateliersError } = await supabase
        .from("mat_ateliers")
        .select("*")
        .order("code");

      if (ateliersError) throw ateliersError;
      setAteliers(ateliersData || []);

      const { data: emplacementsData, error: emplacementsError } =
        await supabase
          .from("mat_emplacements")
          .select(
            `
          *,
          atelier:mat_ateliers(code, nom),
          categorie:mat_categories(id, nom, icon, couleur)
        `
          )
          .order("ordre_affichage");

      if (emplacementsError) throw emplacementsError;
      setEmplacements(emplacementsData || []);

      const { data: outilsData, error: outilsError } = await supabase
        .from("v_mat_outils_complets")
        .select("*");

      if (outilsError) throw outilsError;
      setOutils(outilsData || []);

      const { data: categoriesData, error: categoriesError } = await supabase
        .from("mat_categories")
        .select("*")
        .order("nom");

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
      alert("Erreur lors du chargement des données: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const dataToSave = {
        atelier_id: formData.atelier_id,
        type_emplacement: formData.type_emplacement,
        nom: formData.nom,
        ordre_affichage: parseInt(formData.ordre_affichage),
        categorie_id: formData.categorie_id || null,
      };

      if (editingId) {
        const { error } = await supabase
          .from("mat_emplacements")
          .update(dataToSave)
          .eq("id", editingId);

        if (error) throw error;
        alert("Emplacement modifié avec succès !");
      } else {
        const { error } = await supabase
          .from("mat_emplacements")
          .insert([dataToSave]);

        if (error) throw error;
        alert("Emplacement créé avec succès !");
      }

      resetForm();
      await chargerDonnees();
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur: " + error.message);
    }
  };

  const resetForm = useCallback(() => {
    setFormData(defaultFormData);
    setShowForm(false);
    setEditingId(null);
  }, []);

  const handleEdit = useCallback(
    (emplacement) => {
      if (showForm && editingId === emplacement.id) {
        return;
      }

      setFormData({
        atelier_id: emplacement.atelier_id || "",
        categorie_id: emplacement.categorie_id || "",
        type_emplacement: emplacement.type_emplacement || "armoire",
        nom: emplacement.nom || "",
        ordre_affichage: emplacement.ordre_affichage || 1,
      });
      setEditingId(emplacement.id);
      setViewingEmplacement(null);
      setShowForm(true);
    },
    [showForm, editingId]
  );

  const handleView = useCallback((emplacement) => {
    setViewingEmplacement(emplacement);
    setShowForm(false);
    setEditingId(null);
  }, []);

  // Fonction pour ajouter un outil dans l'emplacement ouvert
  const handleAjouterOutil = useCallback(() => {
    if (!viewingEmplacement) {
      console.error('Aucun emplacement sélectionné');
      return;
    }
    
    console.log('📝 Ajouter outil dans:', viewingEmplacement.nom);
    
    // Si la prop onAddOutilInArmoire existe, l'utiliser
    if (onAddOutilInArmoire) {
      onAddOutilInArmoire(viewingEmplacement);
    } else if (onEditOutil) {
      // Sinon, utiliser onEditOutil avec null (mode ajout)
      onEditOutil(null, viewingEmplacement);
    } else {
      console.error('Aucune fonction disponible pour ajouter un outil');
      alert('Fonction d\'ajout non disponible');
    }
  }, [viewingEmplacement, onAddOutilInArmoire, onEditOutil]);

  // Fonction pour ouvrir le modal photo d'armoire
  const handlePhotoArmoire = useCallback((emplacement) => {
    console.log('📸 Ouvrir photo armoire:', emplacement?.nom);
    setArmoireSelectionnee(emplacement);
    setShowPhotoArmoire(true);
  }, []);

  // Fonction callback après mise à jour photo armoire
  const handlePhotoArmoireUpdate = useCallback(() => {
    console.log('✅ Photo armoire mise à jour');
    setShowPhotoArmoire(false);
    setArmoireSelectionnee(null);
    // Recharger données pour rafraîchir l'affichage
    chargerDonnees();
  }, []);

  const handleDeleteEmplacement = async (id, nom) => {
    const { count } = await supabase
      .from("mat_outils")
      .select("id", { count: "exact", head: true })
      .eq("emplacement_id", id);

    if (count > 0) {
      alert(
        `Impossible de supprimer "${nom}" : ${count} outil(s) sont dans cet emplacement.`
      );
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir supprimer "${nom}" ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("mat_emplacements")
        .delete()
        .eq("id", id);

      if (error) throw error;
      alert("Emplacement supprimé avec succès !");
      chargerDonnees();
    } catch (error) {
      console.error("Erreur suppression:", error);
      alert("Erreur: " + error.message);
    }
  };

  const handleDeleteOutil = async (outil) => {
    if (!confirm(`Supprimer "${outil.nom}" de cet emplacement ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from("mat_outils")
        .delete()
        .eq("id", outil.id);

      if (error) throw error;
      alert("Outil supprimé avec succès !");

      await chargerDonnees();

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Erreur suppression outil:", error);
      alert("Erreur: " + error.message);
    }
  };

  const handleEditOutil = (outil) => {
    setViewingEmplacement(null);

    if (onEditOutil) {
      onEditOutil(outil);
    }
  };

  const handleCancel = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const emplacementsParAtelier = ateliers.map((atelier) => ({
    ...atelier,
    emplacements: emplacements.filter((e) => e.atelier_id === atelier.id),
  }));

  const outilsEmplacement =
    viewingEmplacement ?
      outils.filter((o) => o.emplacement_id === viewingEmplacement.id)
    : [];

  const statsCategories =
    viewingEmplacement ?
      categories
        .map((cat) => ({
          ...cat,
          count: outilsEmplacement.filter((o) => o.categorie_id === cat.id)
            .length,
        }))
        .filter((cat) => cat.count > 0)
    : [];

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-gray-400'>Chargement...</div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold text-white'>
            Gestion des Emplacements
          </h2>
          <p className='text-gray-400 mt-1'>
            {emplacements.length} emplacement(s) dans {ateliers.length}{" "}
            atelier(s)
          </p>
        </div>

        {!showForm && !viewingEmplacement && (
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'>
            <Plus className='w-5 h-5' />
            Nouvel emplacement
          </button>
        )}

        {viewingEmplacement && (
          <button
            onClick={() => setViewingEmplacement(null)}
            className='flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors'>
            <X className='w-5 h-5' />
            Fermer
          </button>
        )}
      </div>

      {/* Vue détaillée d'un emplacement */}
      {viewingEmplacement && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className='bg-slate-800 rounded-lg p-6 border border-slate-700'>
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-4'>
              <div className='w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center'>
                <Archive className='w-8 h-8 text-white' />
              </div>
              <div>
                <h3 className='text-2xl font-bold text-white'>
                  {viewingEmplacement.nom}
                </h3>
                <p className='text-gray-400'>
                  {viewingEmplacement.atelier?.code} -{" "}
                  {viewingEmplacement.atelier?.nom} •{" "}
                  {viewingEmplacement.type_emplacement}
                </p>
                {viewingEmplacement.categorie && (
                  <div className='flex items-center gap-2 mt-2'>
                    <span className='text-sm text-gray-400'>
                      Catégorie principale :
                    </span>
                    <span
                      className='flex items-center gap-1 text-sm px-2 py-1 rounded'
                      style={{
                        backgroundColor:
                          viewingEmplacement.categorie.couleur + "30",
                        color: viewingEmplacement.categorie.couleur,
                      }}>
                      <IconDisplay
                        iconName={viewingEmplacement.categorie.icon}
                        className='w-3 h-3'
                      />
                      {viewingEmplacement.categorie.nom}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className='text-right'>
              <p className='text-4xl font-bold text-white'>
                {outilsEmplacement.length}
              </p>
              <p className='text-gray-400'>outil(s)</p>
            </div>
          </div>

          {/* Bouton ajouter outil dans cette armoire */}
          {onAddOutilInArmoire && (
            <div className='mb-6'>
              <button
                onClick={() => onAddOutilInArmoire(viewingEmplacement)}
                className='w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors'>
                <Plus className='w-5 h-5' />
                Ajouter un outil dans cette armoire
              </button>
            </div>
          )}

          {/* Boutons actions armoire */}
          <div className='flex gap-3'>
            {/* Bouton Photo Armoire - NOUVEAU */}
            <button
              onClick={() => handlePhotoArmoire(viewingEmplacement)}
              className='px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all'>
              <Camera className='w-5 h-5' />
              Photo de l'armoire
            </button>

            {/* Bouton Ajouter outil - EXISTANT */}
            <button
              onClick={handleAjouterOutil}
              className='px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all'>
              <Plus className='w-5 h-5' />
              Ajouter un outil
            </button>
          </div>

          {/* Stats par catégorie */}
          {statsCategories.length > 0 && (
            <div className='mb-6'>
              <h4 className='text-lg font-semibold text-white mb-3 flex items-center gap-2'>
                <FolderOpen className='w-5 h-5' />
                Répartition par catégorie
              </h4>
              <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                {statsCategories.map((cat) => (
                  <div key={cat.id} className='bg-slate-700/50 rounded-lg p-3'>
                    <div
                      className='w-8 h-8 rounded flex items-center justify-center mb-2'
                      style={{ backgroundColor: cat.couleur }}>
                      <IconDisplay
                        iconName={cat.icon}
                        className='w-4 h-4 text-white'
                      />
                    </div>
                    <p className='text-white font-semibold'>{cat.count}</p>
                    <p className='text-xs text-gray-400'>{cat.nom}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Liste des outils AVEC BOUTONS D'ACTION */}
          <div>
            <h4 className='text-lg font-semibold text-white mb-3 flex items-center gap-2'>
              <Package className='w-5 h-5' />
              Outils dans cet emplacement
            </h4>

            {outilsEmplacement.length === 0 ?
              <div className='text-center py-12 bg-slate-700/30 rounded-lg'>
                <Package className='w-12 h-12 text-gray-500 mx-auto mb-3' />
                <p className='text-gray-400'>
                  Aucun outil dans cet emplacement
                </p>
                <p className='text-gray-500 text-sm mt-2'>
                  Utilisez "Ajouter un outil" pour ajouter des outils ici
                </p>
              </div>
            : <div className='space-y-2'>
                {outilsEmplacement.map((outil) => (
                  <div
                    key={outil.id}
                    className='flex items-center gap-4 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors group'>
                    {outil.photo_url ?
                      <img
                        src={outil.photo_url}
                        alt={outil.nom}
                        className='w-16 h-16 rounded object-cover'
                      />
                    : <div className='w-16 h-16 rounded bg-slate-600 flex items-center justify-center'>
                        <Package className='w-8 h-8 text-gray-400' />
                      </div>
                    }

                    <div className='flex-1'>
                      <h5 className='text-white font-semibold'>{outil.nom}</h5>
                      <p className='text-sm text-gray-400'>{outil.reference}</p>
                      <div className='flex items-center gap-2 mt-1'>
                        <span
                          className='text-xs px-2 py-1 rounded'
                          style={{
                            backgroundColor: outil.categorie_couleur + "30",
                            color: outil.categorie_couleur,
                          }}>
                          {outil.categorie_nom}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            outil.disponible ?
                              "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                          }`}>
                          {outil.disponible ? "Disponible" : "Emprunté"}
                        </span>
                      </div>
                    </div>

                    <div className='flex items-center gap-4'>
                      <div className='text-right'>
                        <p className='text-white font-semibold'>
                          Qté: {outil.quantite}
                        </p>
                        <p className='text-sm text-gray-400'>{outil.etat}</p>
                      </div>

                      {/* BOUTONS D'ACTION SUR L'OUTIL */}
                      <div className='flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                        <button
                          type='button'
                          onClick={() => handleEditOutil(outil)}
                          className='p-2 text-blue-400 hover:bg-slate-600 rounded transition-colors'
                          title='Modifier cet outil'>
                          <Edit2 className='w-4 h-4' />
                        </button>
                        <button
                          type='button'
                          onClick={() => handleDeleteOutil(outil)}
                          className='p-2 text-red-400 hover:bg-slate-600 rounded transition-colors'
                          title='Supprimer cet outil'>
                          <Trash2 className='w-4 h-4' />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            }
          </div>
        </motion.div>
      )}

      {/* Formulaire édition emplacement */}
      {showForm && (
        <motion.div
          key='form'
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className='bg-slate-800 rounded-lg p-6 border border-slate-700'>
          <h3 className='text-xl font-semibold text-white mb-4'>
            {editingId ? "Modifier l'emplacement" : "Nouvel emplacement"}
          </h3>

          <form onSubmit={handleSubmit} className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-300 mb-2'>
                Atelier *
              </label>
              <select
                value={formData.atelier_id}
                onChange={(e) =>
                  setFormData({ ...formData, atelier_id: e.target.value })
                }
                className='w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500'
                required>
                <option value=''>-- Sélectionner un atelier --</option>
                {ateliers.map((atelier) => (
                  <option key={atelier.id} value={atelier.id}>
                    {atelier.code} - {atelier.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-2'>
                Catégorie principale (optionnel)
              </label>
              <select
                value={formData.categorie_id}
                onChange={(e) =>
                  setFormData({ ...formData, categorie_id: e.target.value })
                }
                className='w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500'>
                <option value=''>-- Aucune catégorie --</option>
                {categories.map((categorie) => (
                  <option key={categorie.id} value={categorie.id}>
                    {categorie.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-2'>
                Type d'emplacement *
              </label>
              <select
                value={formData.type_emplacement}
                onChange={(e) =>
                  setFormData({ ...formData, type_emplacement: e.target.value })
                }
                className='w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500'
                required>
                <option value='armoire'>Armoire</option>
                <option value='servante'>Servante</option>
                <option value='etagere'>Étagère</option>
                <option value='zone'>Zone</option>
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-2'>
                Nom de l'emplacement *
              </label>
              <input
                type='text'
                value={formData.nom}
                onChange={(e) =>
                  setFormData({ ...formData, nom: e.target.value })
                }
                className='w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500'
                placeholder='Ex: Armoire M4 - Diagnostic'
                required
              />
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-300 mb-2'>
                Ordre d'affichage
              </label>
              <input
                type='number'
                value={formData.ordre_affichage}
                onChange={(e) =>
                  setFormData({ ...formData, ordre_affichage: e.target.value })
                }
                className='w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500'
                min='1'
              />
            </div>

            <div className='flex gap-3'>
              <button
                type='submit'
                className='flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'>
                <Save className='w-4 h-4' />
                {editingId ? "Modifier" : "Créer"}
              </button>
              <button
                type='button'
                onClick={handleCancel}
                className='flex items-center gap-2 px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors'>
                <X className='w-4 h-4' />
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Liste par atelier */}
      {!viewingEmplacement && (
        <div className='space-y-6'>
          {emplacementsParAtelier.map((atelier) => (
            <div
              key={atelier.id}
              className='bg-slate-800 rounded-lg p-6 border border-slate-700'>
              <div className='flex items-center gap-3 mb-4'>
                <div className='w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold'>
                  {atelier.code}
                </div>
                <div>
                  <h3 className='text-lg font-semibold text-white'>
                    {atelier.nom}
                  </h3>
                  <p className='text-sm text-gray-400'>
                    {atelier.emplacements.length} emplacement(s)
                  </p>
                </div>
              </div>

              {atelier.emplacements.length > 0 ?
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {atelier.emplacements.map((emplacement) => {
                    const nbOutils = outils.filter(
                      (o) => o.emplacement_id === emplacement.id
                    ).length;

                    return (
                      <div
                        key={emplacement.id}
                        className='bg-slate-700/50 rounded-lg p-4 hover:bg-slate-700 transition-colors'>
                        <div className='flex items-start justify-between mb-2'>
                          <div className='flex items-center gap-3 flex-1'>
                            <Archive className='w-5 h-5 text-blue-400 flex-shrink-0' />
                            <div className='min-w-0'>
                              <h4 className='text-white font-medium truncate'>
                                {emplacement.nom}
                              </h4>
                              <p className='text-xs text-gray-400 mt-1'>
                                {emplacement.type_emplacement} • Ordre:{" "}
                                {emplacement.ordre_affichage}
                              </p>
                              {emplacement.categorie && (
                                <span
                                  className='inline-flex items-center gap-1 text-xs px-2 py-1 rounded mt-1'
                                  style={{
                                    backgroundColor:
                                      emplacement.categorie.couleur + "30",
                                    color: emplacement.categorie.couleur,
                                  }}>
                                  <IconDisplay
                                    iconName={emplacement.categorie.icon}
                                    className='w-3 h-3'
                                  />
                                  {emplacement.categorie.nom}
                                </span>
                              )}
                              <p className='text-xs text-blue-400 mt-1'>
                                {nbOutils} outil(s)
                              </p>
                            </div>
                          </div>

                          <div className='flex gap-1 flex-shrink-0'>
                            <button
                              type='button'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(emplacement);
                              }}
                              className='p-1.5 text-green-400 hover:bg-slate-800 rounded transition-colors'
                              title='Voir les outils'>
                              <Eye className='w-4 h-4' />
                            </button>
                            <button
                              type='button'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(emplacement);
                              }}
                              className='p-1.5 text-blue-400 hover:bg-slate-800 rounded transition-colors'
                              title="Modifier l'emplacement">
                              <Edit2 className='w-4 h-4' />
                            </button>
                            <button
                              type='button'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEmplacement(
                                  emplacement.id,
                                  emplacement.nom
                                );
                              }}
                              className='p-1.5 text-red-400 hover:bg-slate-800 rounded transition-colors'
                              title="Supprimer l'emplacement">
                              <Trash2 className='w-4 h-4' />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              : <div className='text-center py-8 text-gray-400'>
                  <AlertCircle className='w-8 h-8 mx-auto mb-2 opacity-50' />
                  <p className='text-sm'>Aucun emplacement dans cet atelier</p>
                </div>
              }
            </div>
          ))}
        </div>
      )}

      {ateliers.length === 0 && (
        <div className='text-center py-12 text-gray-400'>
          <AlertCircle className='w-12 h-12 mx-auto mb-4 opacity-50' />
          <p>Aucun atelier configuré.</p>
        </div>
      )}

      {/* Modal Photo Armoire */}
      <AnimatePresence>
        {showPhotoArmoire && armoireSelectionnee && (
          <PhotoArmoireManager
            emplacement={armoireSelectionnee}
            onUpdate={handlePhotoArmoireUpdate}
            onClose={() => setShowPhotoArmoire(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GestionEmplacements;